"use client";

import Link from "next/link";
import { usePathname, useSearchParams } from "next/navigation";
import { useTranslations } from "next-intl";
import { cn } from "@/lib/utils";
import { buttonStyles } from "@/ui/components/ui/button";

interface NumberedPaginationProps {
	page: number;
	totalPages: number;
}

type PageToken = number | "gap";

/** 1 … p-1 p p+1 … total — short lists render in full. */
function buildPageWindow(page: number, totalPages: number): PageToken[] {
	if (totalPages <= 7) {
		return Array.from({ length: totalPages }, (_, i) => i + 1);
	}
	const wanted = [...new Set([1, page - 1, page, page + 1, totalPages])]
		.filter((p) => p >= 1 && p <= totalPages)
		.sort((a, b) => a - b);
	const out: PageToken[] = [];
	let prev = 0;
	for (const p of wanted) {
		if (p - prev > 1) out.push("gap");
		out.push(p);
		prev = p;
	}
	return out;
}

/**
 * Page-number pagination for server-filtered listings (?page=N).
 * Preserves every filter param; page 1 uses the clean URL. Legacy cursor
 * params are dropped so old links converge on the canonical shape.
 */
export function NumberedPagination({ page, totalPages }: NumberedPaginationProps) {
	const pathname = usePathname();
	const searchParams = useSearchParams();
	const t = useTranslations("pagination");

	if (totalPages <= 1) return null;

	const hrefFor = (target: number): string => {
		const params = new URLSearchParams(searchParams);
		params.delete("cursor");
		params.delete("direction");
		if (target <= 1) {
			params.delete("page");
		} else {
			params.set("page", String(target));
		}
		const qs = params.toString();
		return qs ? `${pathname}?${qs}` : pathname;
	};

	const hasPrev = page > 1;
	const hasNext = page < totalPages;

	return (
		<nav aria-label={t("label")} className="flex items-center justify-center gap-x-2 px-4 pt-12">
			<Link
				href={hasPrev ? hrefFor(page - 1) : "#"}
				className={
					hasPrev
						? buttonStyles({ variant: "outline-solid", size: "default" })
						: buttonStyles({
								variant: "outline-solid",
								size: "default",
								className: "pointer-events-none text-muted-foreground opacity-60",
							})
				}
				aria-disabled={!hasPrev}
			>
				{t("previous")}
			</Link>

			<span className="hidden items-center gap-x-1 sm:flex">
				{buildPageWindow(page, totalPages).map((token, i) =>
					token === "gap" ? (
						<span key={`gap-${i}`} aria-hidden className="px-1 text-sm text-muted-foreground">
							…
						</span>
					) : (
						<Link
							key={token}
							href={hrefFor(token)}
							aria-label={t("page", { page: token })}
							aria-current={token === page ? "page" : undefined}
							className={cn(
								"inline-flex h-10 min-w-10 items-center justify-center rounded-md px-2 text-sm tabular-nums transition-colors",
								token === page
									? "bg-foreground font-semibold text-background"
									: "text-foreground/75 hover:bg-muted hover:text-foreground",
							)}
						>
							{token}
						</Link>
					),
				)}
			</span>

			{/* Mobile: compact "page X of Y" instead of the number row */}
			<span className="px-2 text-sm tabular-nums text-muted-foreground sm:hidden">
				{t("pageOf", { page, total: totalPages })}
			</span>

			<Link
				href={hasNext ? hrefFor(page + 1) : "#"}
				className={
					hasNext
						? buttonStyles({ variant: "default", size: "default" })
						: buttonStyles({
								variant: "outline-solid",
								size: "default",
								className: "pointer-events-none text-muted-foreground opacity-60",
							})
				}
				aria-disabled={!hasNext}
			>
				{t("next")}
			</Link>
		</nav>
	);
}
